import { InvestorCapitalAccount } from '../../models/capital-account/InvestorCapitalAccount';
import { CustomAllocation } from '../../models/capital-account/CustomAllocation';
import { ItemAllocationMethod } from '../../models/capital-account/ItemAllocationMethod';
import { FinancialStatementPart } from '../../models/common/FinancialStatementPart';
import { InvestorCapitalAccountItemValue } from '../../models/capital-account/InvestorCapitalAccountItemValue';
import { CustomAllocationValue } from '../../models/capital-account/CustomAllocationValue';
import {
  ItemManualValueData,
} from '../../../excel/templates/financial-statement/capital-account/item-manual-values-parser';
import { InvestorCapitalAccountCarriedInterest } from '../../models/capital-account/InvestorCapitalAccountCarriedInterest';
import { CarriedInterestSelectorData } from '../../models/DTOs/capital-account/CarriedInterestSelectorData';

export class ShowCA {
  lps = false;
  gps = false;
  thinkTwiceDeleteCustomAllocation = false;
  items = false;
  investors = false;

  modals = {
    manageInvestors: false,
    customAllocation: false,
    crudInvestor: false,
    bulkUploadManualValues: false,
    thinkTwiceDeleteInvestor: false,
    thinkTwiceDeleteCustomAllocation: false,
    carriedInterestUD: false,
  };
}

/* eslint-disable @typescript-eslint/naming-convention */
/**
 * An enumeration representing the calculation methods available on the capital account page.
 * The chosen method determines the approach for value calculations.
 * The 'MANUAL' method might require user input or fetch values from the database.
 */
export enum AllocationMethod {
  HOLDING = 'Holding, %',
  HOLDING_WITHOUT_GP = 'Holding without GP, %',
  MANUAL = 'Manual',
}

/**
 * Class, representing all data related to an investor on the capital account page.
 */
export class InvestorDataCA {
  investor: InvestorCapitalAccount;
  itemValue: { [itemId: string]: number };
  manualValue: { [itemId: string]: number | null };
  customAllocationValue: { [customAllocationUuid: string]: number | null };
  allocationMethod: { [itemId: string]: AllocationMethod };
  carriedInterest: InvestorCarriedInterest;
  holding: number | null;
  holdingWithoutGP: number | null;
  accumulatedResults: number;
  nav: number | null;
  ngl: number | null;
  tvpi: number | null;

  constructor(investor: InvestorCapitalAccount) {
    this.investor = investor;
    this.itemValue = {};
    this.manualValue = {};
    this.customAllocationValue = {};
    this.allocationMethod = {};
    this.carriedInterest = new InvestorCarriedInterest();
    this.holding = null;
    this.holdingWithoutGP = null;
    this.nav = null;
    this.ngl = null;
    this.tvpi = null;
  }
}

/**
 * Class, representing all data related to an investors carried interest on the capital account page.
 * The values are dependent on CarriedInterestSelector.selectedMethod (calculated or manual).
 * If calculated, the calculated value would be investors *Accumulated results* multiplied by *manualPct* if present, otherwise
 * multiplied by *CarriedInterestCA.inputPct*
 */
export class InvestorCarriedInterest {
  manualValue: number = null;
  manualPct: number = null;
  calculatedValue: number = null;
}


export interface ModalManageCustomAllocationObject {
  description: string;
  key: 'new' | 'edit' | 'delete';
  customAllocation: CustomAllocation;
  existingNames: string[];
}

export class PartnersTotals {
  constructor(
    public lps: number = 0,
    public gps: number = 0,
    public all: number = 0,
  ) {
  }
}


export class CarriedInterestTotals extends PartnersTotals {
  constructor(
    lps: number = 0,
    gps: number = 0,
    all: number = 0,
    public ciValue: number = 0,
    public subtotalGps: number = 0,
    public grandTotal: number = 0,
  ) {
    super(lps, gps, all);
  }
}


export class CapitalAccountTotals {
  commitment: PartnersTotals = new PartnersTotals();
  holding: PartnersTotals = new PartnersTotals();
  item: { [itemId: string]: PartnersTotals } = {};
  customAllocation: { [customAllocationUuid: string]: PartnersTotals } = {};
  /**
   * Represents the sum of Profits and Losses (without Partners' capital) items' values
   * and custom allocation values.
   */
  accumulatedResults: PartnersTotals = new PartnersTotals();
  /**
   * Carried interest is determined based on the Carried interest selector allocation method.
   * If Calculated: Represents the investor's accumulated results multiplied on a common or (if set) a manual percentage.
   * Else: takes manual value.
   */
  carriedInterest: CarriedInterestTotals = new CarriedInterestTotals();
  /**
   * Net Asset Value (NAV): Represents the sum of all item values, custom allocation values and Carried interest.
   */
  nav: PartnersTotals = new PartnersTotals();
  /**
   * Net Gain/Loss (NGL): Represents the sum of Profits and Losses (without Partners' capital) from item values
   * and custom allocation values(Accumulated results) minus Carried interest.
   */
  ngl: PartnersTotals = new PartnersTotals();
  /**
   * Total Value to Paid-In (TVPI):
   * (NAV + |Distribution| - |Contribution received in advance|) / (Contribution - |Due to capital contribution|)
   */
  tvpi: PartnersTotals = new PartnersTotals();
}


export enum CarriedInterestAllocationMethod {
  MANUAL = 'Manual',
  CALCULATED = 'Calculated',
}

/**
 * Represents the selector object for the carried interest on the capital account page.
 * Contains list of possible methods and the selected method.
 */
export class CarriedInterestSelector {
  selectedMethod: CarriedInterestAllocationMethod;
  methods: CarriedInterestAllocationMethod[];
  constructor() {
    this.selectedMethod = CarriedInterestAllocationMethod.MANUAL; // set manual by default
    this.methods = [CarriedInterestAllocationMethod.CALCULATED, CarriedInterestAllocationMethod.MANUAL];
  }
}

/**
 * Represents the carried interest details in a capital account context.
 * This class encapsulates all the necessary components to manage and display carried interest information for a capital account.
 *
 * Properties:
 * - **selector**: An instance of CarriedInterestSelector which holds the current selection
 * and available methods for carried interest calculation.
 * - **inputPct**: A number representing the input percentage used in the carried interest calculation.
 * - **dbSelectorData*: An instance of CarriedInterestSelectorData,
 * containing the data fetched from the database for the carried interest selector.
 * - **dbInvestorsValues**: An array of InvestorCapitalAccountCarriedInterest,
 * each representing the carried interest details for an individual investor.
 */
export class CarriedInterestCA {
  selector: CarriedInterestSelector;
  manualPct: number;
  dbSelectorData: CarriedInterestSelectorData;
  dbInvestorsValues: InvestorCapitalAccountCarriedInterest[];
  constructor() {
    this.selector = new CarriedInterestSelector();
    this.manualPct = undefined;
    this.dbSelectorData = undefined;
    this.dbInvestorsValues = [];
  }
}

export class CapitalAccountData {
  investorsData: InvestorDataCA[];
  itemAllocationMethods: ItemAllocationMethod[];
  itemToAllocationMethod: { [itemId: string]: AllocationMethod };
  partnersCapital: FinancialStatementPart;
  profits: FinancialStatementPart;
  losses: FinancialStatementPart;
  customAllocations: CustomAllocation[];
  customAllocationValues: CustomAllocationValue[];
  carriedInterest: CarriedInterestCA;
  manualValues: InvestorCapitalAccountItemValue[];
  totals: CapitalAccountTotals;
  tvpiItemsIds: TvpiItemsIds;

  constructor() {
    this.investorsData = [];
    this.itemAllocationMethods = [];
    this.itemToAllocationMethod = {};
    this.partnersCapital = null;
    this.profits = null;
    this.losses = null;
    this.customAllocations = [];
    this.customAllocationValues = [];
    this.manualValues = [];
    this.totals = new CapitalAccountTotals();
    this.tvpiItemsIds = new TvpiItemsIds();
  }
}

export class TvpiItemsIds {
  distribution: number = null;
  contributionReceivedInAdvance: number = null;
  contribution: number = null;
  dueToCapitalContribution: number = null;
}

export interface ItemAllocationUpdate {
  itemManualValueData: ItemManualValueData;
  itemAllocationMethod: ItemAllocationMethod;
}

export interface CustomAllocationUpdate {
  itemManualValueData: ItemManualValueData;
  customAllocation: CustomAllocation;
}

